import { ArrowRight, KeyRound, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { errorMessage } from '@/lib/query';

export function AuthLayout({ children, title, subtitle }: { children: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#1c130d] lg:block">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_20%,#c8a16555,transparent_55%),radial-gradient(ellipse_at_80%_70%,#7a4e2d88,transparent_60%)]" />
        <svg className="absolute bottom-0 w-full text-[#2a1c12]" viewBox="0 0 1440 320" preserveAspectRatio="none" aria-hidden>
          <path fill="currentColor" d="M0 224 C 240 160 480 288 720 240 C 960 192 1200 128 1440 192 L1440 320 L0 320 Z" />
          <path fill="#3a271a" d="M0 272 C 300 230 520 300 800 270 C 1080 240 1260 260 1440 250 L1440 320 L0 320 Z" />
        </svg>
        <div className="relative flex h-full flex-col justify-between p-12 text-[#f3e8d8]">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" className="size-10" alt="" />
            <span className="font-display text-xl font-semibold">Stable Platform</span>
          </div>
          <div className="max-w-md">
            <h2 className="font-display text-[40px] font-semibold leading-[1.1]">Every ride, horse and member — in one place.</h2>
            <p className="mt-4 text-[15px] text-[#e7d7c1]/80">Bookings, memberships, payments, horse care, staff and growth for modern equestrian stables.</p>
          </div>
          <p className="text-xs text-[#e7d7c1]/50">© {new Date().getFullYear()} Stable Platform</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="mb-8 lg:hidden">
            <img src="/favicon.svg" className="size-11" alt="" />
          </div>
          <h1 className="font-display text-[28px] font-semibold">{title}</h1>
          {subtitle && <p className="mt-1 text-[13.5px] text-muted-foreground">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { loginWithPassword, requestOtp, verifyOtp } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const wrap = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to run your stable.">
      <Tabs defaultValue="email">
        <TabsList className="mb-6 w-full">
          <TabsTrigger value="email" className="flex-1">
            <KeyRound className="me-1.5 inline size-4" /> E-mail
          </TabsTrigger>
          <TabsTrigger value="phone" className="flex-1">
            <Smartphone className="me-1.5 inline size-4" /> Phone code
          </TabsTrigger>
        </TabsList>
        <TabsContent value="email" className="pt-0">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void wrap(() => loginWithPassword(email, password));
            }}
          >
            <Field label="E-mail">
              <Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@stable.com" required autoFocus />
            </Field>
            <Field label="Password">
              <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Field>
            <Button type="submit" size="lg" className="w-full" loading={busy}>
              {t('Sign in')} <ArrowRight className="rtl:rotate-180" />
            </Button>
          </form>
        </TabsContent>
        <TabsContent value="phone" className="pt-0">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!codeSent)
                void wrap(async () => {
                  const r = await requestOtp(phone);
                  setCodeSent(true);
                  if (r.devCode) {
                    setCode(r.devCode);
                    toast.info(`Development code: ${r.devCode}`);
                  } else toast.success('Code sent by SMS');
                });
              else void wrap(() => verifyOtp(phone, code));
            }}
          >
            <Field label="Mobile number" hint="UAE numbers can start with 05…">
              <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+971 50 123 4567" required disabled={codeSent} autoFocus />
            </Field>
            {codeSent && (
              <Field label="6-digit code">
                <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className="text-center font-mono text-lg tracking-[0.5em]" autoFocus />
              </Field>
            )}
            <Button type="submit" size="lg" className="w-full" loading={busy}>
              {codeSent ? t('Sign in') : 'Send code'} <ArrowRight className="rtl:rotate-180" />
            </Button>
            {codeSent && (
              <button type="button" className="w-full text-center text-[13px] text-muted-foreground hover:text-foreground" onClick={() => { setCodeSent(false); setCode(''); }}>
                Use a different number
              </button>
            )}
          </form>
        </TabsContent>
      </Tabs>
    </AuthLayout>
  );
}
